import { Component, ViewChild, ElementRef, viewChild, effect, signal, inject, InjectionToken, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router, NavigationEnd, RouterLink } from '@angular/router';
import { FormArray, FormGroup, FormControl, FormBuilder, AbstractControl, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { Subject, fromEvent, BehaviorSubject, filter, debounceTime, distinctUntilChanged, Subscription, switchAll, map, switchMap, tap, Observable, of, catchError, interval, finalize } from 'rxjs';

import { SpinnerService } from '@app/shared/spinner.service';
import { AlertService } from '@app/shared/alert.service';
import { PrintService } from '@app/shared/print.service';
import { QuotationService } from '@app/sales/quotation/quotation.service';
import { PartnersService } from '@app/common/partners/partners.service';
import { SalesService } from '@app/sales/sales.service';
import { FseService } from '@app/fse/fse.service';

import { MatFormFieldModule } from '@angular/material/form-field';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatExpansionModule } from '@angular/material/expansion';
import { A11yModule } from '@angular/cdk/a11y';
import { DIALOG_DATA } from '@angular/cdk/dialog';

import { C_value } from '@app/shared/_models/common';

import { RecipientComponent } from '@app/fse/recipient/recipient.component';

import * as util from '@app/shared/util';

import { LoginService } from '@app/common/login.service';
import { ValuesService } from '@app/common/values/values.service';
//import { Event } from '@app/shared/_models/common';

import { environment } from '@app/../environments/environment';
import * as constants from '@app/shared/constants';

import { MercalinkFselibService, LireCVHTTPResponse, EmpreinteCV, qualites$, Beneficiaire, ApiclientService, ConfigArea, dao } from 'mercalink-fselib';

export interface InputDialogData {
  area_baseurl: string;
  server_url: string;
  userid: number;
  numIdentFact: string;
  isTokenExpired: boolean; // Fses can pass isTokenExpired, meaning just a context refresh
}

export interface OutputDialogData {
  mode: number; // mode:0 CV, 1 ApCV QR code, 2 ApCV NFC, 3 NO CV=dégradé
  index?: number;
  numIdentFact: string;
  empreinteCV?: EmpreinteCV; // For mode 0
  contexteAuthentificationApCV?: dao.ContexteAuthentificationApCV; // For mode 1
  isTokenExpired: boolean; // Pass it to BsDialog, just so it knows
}

@Component({
  selector: 'app-recipients',
  standalone: true,
  imports: [A11yModule, FormsModule, ReactiveFormsModule, MatButtonModule, MatExpansionModule, MatTooltipModule, MatTabsModule, MatIcon, MatFormFieldModule, MatInputModule, MatAutocompleteModule],
  templateUrl: './recipients.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./recipients.component.scss' /* '../../styles-custom.scss'*/],
})
export class RecipientsComponent {
  readonly dialogRef = inject(MatDialogRef<RecipientsComponent>);
  readonly dialogdata = inject<InputDialogData>(MAT_DIALOG_DATA as any); // 'as any' tricks the compiler
  public empreinteCVInserted: EmpreinteCV | undefined; // Empreinte from inserted card
  public map_qualities = new Map(); // Qualités organised into a map for fast access
  public qualities: C_value[] = [];
  public errormsg: string | undefined = undefined;
  public loadingmsg: string | undefined = undefined;
  public warningmsg: string | undefined = undefined;
  public countdown: number | undefined = undefined;
  private countdownSubscription: Subscription | undefined;
  public mouseoverSubject: Subject<number> = new Subject();
  public mouseoutSubject: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private mouseoversubscription!: Subscription;
  private mouseoutsubscription!: Subscription;
  private lastmouseout: boolean = false;
  private subscriptions: Subscription = new Subscription();
  public util = util;
  public individuApCVs: dao.IndividuApCV[] = []; // Result from ApCV call
  public contexteAuthentificationApCV: dao.ContexteAuthentificationApCV | undefined;
  public apCvMode: number | undefined;
  public scanSuccess = false;
  public dao = dao;
  private nfcTimeout = 20; // 20 seconds NFC timeout
  public showHidInput = false; // Manage HID textarea visibility
  @ViewChild('hid') hidElement!: ElementRef<HTMLTextAreaElement>;

  public loginService = inject(LoginService);
  private valuesService = inject(ValuesService);
  private partnersService = inject(PartnersService);
  private salesService = inject(SalesService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private spinner = inject(SpinnerService);
  private alertService = inject(AlertService);
  public dialog = inject(MatDialog);
  private fb = inject(FormBuilder);
  private quotationService = inject(QuotationService);
  private printService = inject(PrintService);
  private mercalinkFselibService = inject(MercalinkFselibService);
  private fseService = inject(FseService);
  private apiclientService = inject(ApiclientService);
  private configarea!: ConfigArea;

  constructor() {
    const station = this.loginService.currentstation;
    if (station && station.data.hasOwnProperty('configarea')) {
      this.configarea = station.data.configarea;
    }
  }
  ngOnInit(): void {
    this.loadingmsg = 'Chargement\nVérifier la fenêtre AreaFSE';
    // console.log(`[RecipientsComponent ngOnInit] dialogdata: ${JSON.stringify(this.dialogdata, null, 2)}`);
    this.subscriptions.add(
      qualites$.subscribe((data: any) => {
        // Load qualités into map_qualities
        /*this.qualities = data;
      this.qualities.map(a => {
        this.map_qualities.set(a.id, a.name);
      });*/
        this.qualities.push(data);
        this.map_qualities.set(data.id, data.name);
      }),
    );

    if (!this.dialogdata.isTokenExpired) {
      // Normal mode, show recipients
      this.subscriptions.add(
        this.mercalinkFselibService
          .lireCV(this.dialogdata.area_baseurl, this.dialogdata.numIdentFact)
          .pipe(
            switchMap((res: LireCVHTTPResponse | undefined) => {
              console.log('[ngOnInit] lireCV: ', res);
              if (res && (res.codeRetour == 0 || res.codeRetour == 1)) {
                // fse call was OK OR No CPS card => message but data is there
                if (res.codeRetour == 1) {
                  // No CPS card => message but data is there, go on
                  this.warningmsg = replaceAsciiCode(res.libRetour);
                  console.log(replaceAsciiCode(res.libRetour));
                }
                this.empreinteCVInserted = res?.empreinteCV; // empreinteCVInserted holds the physically inserted CV data
                this.individuApCVs = [];

                if (this.empreinteCVInserted?.numSecu) {
                  // We got a real empreinteCV
                  this.onDelCtxApCV(); // Destroy AppCV context when a CV is inserted
                  this.apCvMode = dao.constants.MODE_FSE_CV;
                  return this.fseService.postFseCardEvent$(this.dialogdata.server_url, constants.EVENTTYP_INSERT, this.dialogdata.userid, { empreinteCV: this.empreinteCVInserted }).pipe(
                    catchError(e => {
                      // API Server error saving CV
                      const ret = { errorcode: -1, msg: e.error };
                      console.error('ERREUR Serveur : ', e.error);
                      return of(ret);
                    }),
                  );
                } else {
                  // No numSecu => card data inconsistency(?)
                  const ret = { errorcode: -2, msg: 'Données CV incohérentes' };
                  console.error('ERREUR Serveur FSE : ', ret.msg);
                  return of(ret);
                }
              } else if (res?.codeRetour == -1000) {
                // No CV card inserted
                const ret = { errorcode: -1000, msg: res.libRetour };
                console.error('ERREUR Serveur : ', ret.msg);
                return of(ret);
              } else {
                // Some other card reading error
                //this.errormsg = res?.libRetour ?? "ERREUR à la lecture de la CV";
                const ret = { errorcode: -3, msg: res?.libRetour ?? 'ERREUR à la lecture de la CV' };
                console.error('ERREUR Serveur FSE : ', ret.msg);
                return of(ret);
              }
            }),
            catchError(error => {
              // Can't connect to FSE server (probably)
              const ret = { errorcode: -4, msg: JSON.stringify(error) };
              return of(ret);
            }),
          )
          .subscribe({
            next: (data: any) => {
              // Data is coming from postFseCardEvent API server OR custom error above
              this.loadingmsg = undefined;
              if (data?.data) {
                // Response comes from API server
                if (!data?.data?.error_code) {
                  // No error from server => All went well
                  console.log('[lireCV] postFseCardEvent: ', JSON.stringify(data));
                } else {
                  this.errormsg = 'ERREUR Serveur: Création des données patients: ' + JSON.stringify(data);
                  console.error(JSON.stringify(data));
                }
              } else {
                // Response comes from other error above
                if (data.errorcode == -1000) {
                  // No CV card
                  this.warningmsg = data.msg;
                } else {
                  // Some kind of error, just spit it
                  this.errormsg = data.msg;
                }
              }
            },
          }),
      );

      this.mouseoutsubscription = this.mouseoutSubject.subscribe({
        next: (val: any) => {
          //console.log("mouseout: " + val);
          this.lastmouseout = val;
        },
      });

      this.mouseoversubscription = this.mouseoverSubject
        .pipe(
          debounceTime(2000), // Wait for detail opening
          filter(() => this.lastmouseout),
        )
        .subscribe({
          next: (index: number) => {
            console.log('Triggering row: ' + index);
            this.openRecipientDialog(index);
          },
          error: (e: any) => {
            console.log('ERREUR Serveur : ', e.error);
          },
        });
    } else {
      // 'Token expired' mode. Just get a new token
      this.loadingmsg = undefined;
    }
  }
  // Close page =>
  onClose(): void {
    this.dialogRef.close(undefined);
  }

  onNOCVClick(_event: MouseEvent) {
    // No card => mode 3, dégradé
    this.dialogRef.close({ mode: dao.constants.MODE_FSE_DEGRADE, index: undefined, empreinteCV: undefined, numIdentFact: this.dialogdata.numIdentFact } as OutputDialogData);
  }

  onRecipientClick(_event: MouseEvent, index: number, mode: number): void {
    if (mode === dao.constants.MODE_FSE_CV) {
      // CV Mode => use empreinteCVInserted
      this.dialogRef.close({ mode: mode, index: +index, empreinteCV: this.empreinteCVInserted, numIdentFact: this.dialogdata.numIdentFact } as OutputDialogData);
    } else {
      // One of NFC modes
      this.dialogRef.close({ mode: mode, index: +index, empreinteCV: undefined, numIdentFact: this.dialogdata.numIdentFact, contexteAuthentificationApCV: this.contexteAuthentificationApCV } as OutputDialogData);
    }
  }

  onQRClick(_event: MouseEvent) {
    // ApCV QR code click, mode 1
    this.scanSuccess = false;
    this.apCvMode = dao.constants.MODE_FSE_APCV_QR;
    this.individuApCVs = [];
    this.contexteAuthentificationApCV = undefined;
    this.startCountdown(this.nfcTimeout);
    this.processApCV(this.apiclientService.getBarcodeScanapcv$(environment.scanapcv_QR_url).pipe(finalize(() => this.stopCountdown())), dao.constants.MODE_FSE_APCV_QR);
  }

  onQRHIDClick(_event: MouseEvent) {
    // ApCV QR code click, mode 1 (Option HID: a dummy textarea is shown)
    this.scanSuccess = false;
    this.apCvMode = dao.constants.MODE_FSE_APCV_QR;
    this.showHidInput = true;
    this.individuApCVs = [];
    this.contexteAuthentificationApCV = undefined;
    this.startCountdown(this.nfcTimeout);
    // We need a timeout to wait for Angular to render the div (since it was hidden)
    // before we can call .focus() on the element.
    setTimeout(() => {
      if (this.hidElement) {
        this.hidElement.nativeElement.value = ''; // Clear previous
        this.hidElement.nativeElement.focus();
      }
    }, 100);
    //this.processApCV(this.apiclientService.getBarcodeScanapcv$(environment.scanapcv_QR_url), dao.constants.MODE_FSE_APCV_QR);
  }

  onHidScannerInput(event: KeyboardEvent) {
    if (event.key === 'Enter') {
      const token = this.hidElement.nativeElement.value.trim();
      if (token) {
        this.stopCountdown();
        this.showHidInput = false; // Re-hide
        this.processApCV(of({ scan: token }), dao.constants.MODE_FSE_APCV_QR);
      }
    }
  }

  onNFCClick(_event: MouseEvent) {
    // ApCV NFC click, mode 2
    this.scanSuccess = false;
    this.apCvMode = dao.constants.MODE_FSE_APCV_NFC;
    this.individuApCVs = [];
    this.contexteAuthentificationApCV = undefined;
    this.startCountdown(this.nfcTimeout); // Trigger countdown
    this.processApCV(this.apiclientService.getNFCScanapcv$(environment.scanapcv_NFC_url).pipe(finalize(() => this.stopCountdown())), dao.constants.MODE_FSE_APCV_NFC);
  }

  private processApCV(scanObservable$: Observable<any>, mode: number) {
    this.subscriptions.add(
      scanObservable$.subscribe({
        next: (result: any) => {
          if (result?.scan) {
            // Got a token
            this.scanSuccess = true;
            console.log(`initFromApCV, token: ${result.scan}`);
            const config = this.configarea;
            this.subscriptions.add(
              this.mercalinkFselibService.demanderContexteAuthentificationApCV$(this.dialogdata.area_baseurl, this.dialogdata.numIdentFact, config.nomRessLect, config.editeur, config.nomLogiciel, config.versionApCV, config.versionCommerciale, config.numAutorisationApcv, result.scan, config.slotCPS).subscribe({
                next: (res: dao.demanderContexteAuthentificationApCVResponse) => {
                  if (+res.HTTPResponse.codeRetour < 0) {
                    // Area returned an error
                    this.scanSuccess = false;
                    console.error('ApCV call error', res.HTTPResponse.libRetour);
                    util.showError("Erreur lors de l'appel ApCV: " + res.HTTPResponse.libRetour);
                  } else {
                    // Normalize individu to array
                    const ctx = res.HTTPResponse.contexteAuthentificationApCV;
                    if (ctx && ctx.individu && !Array.isArray(ctx.individu)) {
                      ctx.individu = [ctx.individu];
                    }
                    console.log('ApCV Context retrieved:', JSON.stringify(res, null, 2));
                    if (Array.isArray(ctx.individu)) {
                      this.individuApCVs = ctx.individu; // Test just to please the compiler
                      this.empreinteCVInserted = undefined;
                      this.contexteAuthentificationApCV = ctx;
                      this.apCvMode = mode;
                    }
                    if (this.dialogdata.isTokenExpired) {
                      // Called from fses, just needed a token refresh
                      this.dialogRef.close({ isTokenExpired: this.dialogdata.isTokenExpired } as OutputDialogData); // Return non undefined
                    }
                  }
                },
                error: (err: any) => {
                  this.scanSuccess = false;
                  console.error('ApCV call error', err);
                  util.showError("Erreur lors de l'appel ApCV: " + err);
                },
              }),
            );
          } else {
            util.showError('Erreur inattendue à la lecture ApCV');
          }
        },
        error: (err: any) => {
          console.error('ApCV error handled in stream', err);
          if (err.error && err.error.error) {
            util.showError('Erreur ApCV: ' + err.error.error);
          } else if (err instanceof Error) {
            util.showError('Erreur inattendue à la lecture ApCV: ' + err.message);
          } else if (typeof err === 'object') {
            util.showError('Erreur inattendue à la lecture ApCV: ' + JSON.stringify(err));
          } else {
            util.showError('Erreur inattendue à la lecture ApCV: ' + err);
          }
        },
      }),
    );
  }

  onGetCtxApCV() {
    // Restituer le contexte ApCV (get contexte cache in Area)
    this.individuApCVs = []; // Clear previous results
    const config = this.configarea;
    this.subscriptions.add(
      this.mercalinkFselibService.restituerContexteAuthentificationApCV$(this.dialogdata.area_baseurl, this.dialogdata.numIdentFact, config.nomRessLect, config.editeur, config.nomLogiciel, config.versionApCV, config.versionCommerciale, config.numAutorisationApcv, config.slotCPS).subscribe({
        next: (res: dao.demanderContexteAuthentificationApCVResponse) => {
          if (+res.HTTPResponse.codeRetour < 0) {
            // Area returned an error
            console.error('ApCV call error', res.HTTPResponse.libRetour);
            util.showError('Erreur lors de la restitution de ctx ApCV: ' + res.HTTPResponse.libRetour);
          } else {
            // Normalize individu to array
            const ctx = res.HTTPResponse.contexteAuthentificationApCV;
            if (ctx && ctx.individu && !Array.isArray(ctx.individu)) {
              ctx.individu = [ctx.individu];
            }
            console.log('ApCV Context retrieved:', JSON.stringify(res, null, 2));
            this.warningmsg = 'Contexte ApCV restitué';
            if (Array.isArray(ctx.individu)) {
              this.individuApCVs = ctx.individu; // Test just to please the compiler
              this.empreinteCVInserted = undefined;
              this.contexteAuthentificationApCV = ctx;
              this.apCvMode = dao.constants.MODE_FSE_APCV_QR;
            }
          }
        },
        error: (err: any) => {
          console.error('ApCV error handled in stream', err);
          if (err.error && err.error.error) {
            util.showError('Erreur ApCV: ' + err.error.error);
          } else if (err instanceof Error) {
            util.showError('Erreur inattendue à la restitution de ctx ApCV: ' + err.message);
          } else if (typeof err === 'object') {
            util.showError('Erreur inattendue à la restitution de ctx ApCV: ' + JSON.stringify(err));
          } else {
            util.showError('Erreur inattendue à la restitution de ctx ApCV: ' + err);
          }
        },
      }),
    );
  }

  onDelCtxApCV() {
    const config = this.configarea;
    this.subscriptions.add(
      this.mercalinkFselibService.detruireContexteAuthentificationApCV$(this.dialogdata.area_baseurl, this.dialogdata.numIdentFact, config.nomRessLect, config.slotCPS).subscribe({
        next: (res: any) => {
          if (+res.HTTPResponse.codeRetour < 0) {
            // Area returned an error
            console.error('onDelCtxApCV call error', res.HTTPResponse.libRetour);
            util.showError('Erreur lors de la destruction du ctx ApCV: ' + res.HTTPResponse.libRetour);
          } else {
            console.log('ApCV Context deleted:', JSON.stringify(res, null, 2));
            this.warningmsg = 'Contexte ApCV détruit'; // TODO Clear results?
          }
        },
        error: (err: any) => {
          console.error('ApCV error handled in stream', err);
          util.showError('Erreur inattendue à la destruction du ctx ApCV: ' + err);
        },
      }),
    );
  }

  startCountdown(seconds: number) {
    this.countdown = seconds;
    this.countdownSubscription = interval(1000).subscribe(() => {
      if (this.countdown !== undefined && this.countdown > 0) {
        this.countdown--;
      } else {
        this.stopCountdown();
      }
    });
  }

  stopCountdown() {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
    this.countdown = undefined;
  }

  getQualite(index: number): string {
    if (this.individuApCVs.length > 0) {
      const val = this.individuApCVs[index].qualite;
      const mapped = this.map_qualities.get(Number(val));
      return mapped ? mapped : val;
    }
    //console.log("[getQualite] index - this.empreinteCVInserted?.beneficiaire[index]", index, JSON.stringify(this.empreinteCVInserted?.beneficiaire[+index]));
    return this.map_qualities.get(this.empreinteCVInserted?.beneficiaire[+index].qualite ? +this.empreinteCVInserted?.beneficiaire[+index].qualite : 'Inconnu');
  }

  getBeneficiaire(index: number): string {
    let ret: string = this.getQualite(index);
    if (this.individuApCVs.length > 0) {
      const ind = this.individuApCVs[index];
      if (ind.nomUsage) ret += ' (' + ind.nomUsage + ')';
      if (ind.nomFamille) ret += ' ' + ind.nomFamille;
      if (ind.prenom) ret += ' ' + ind.prenom;
      return ret;
    }
    const ind = this.empreinteCVInserted?.beneficiaire[+index];
    if (!ind) return '';
    if (ind.nomUsuel) ret += ' ' + ind.nomUsuel;
    if (ind.prenom) ret += ' ' + ind.prenom;
    return ret;
  }

  openRecipientDialog(index: number) {
    //this.mouseoversubscription.unsubscribe();									// Prevents detail page from opening FIXME do not work
    const dialogRef = this.dialog.open(RecipientComponent, {
      //width: '1280px',
      //height: '900px',
      maxHeight: '900px',
      maxWidth: '600px',
      panelClass: 'dialog-container-wide', // TODO does not work
      data: {
        beneficiaire: this.empreinteCVInserted?.beneficiaire[index], // TODO what if this.empreinteCVInserted is undefined? (existing FSE)
        empreinteCV: this.empreinteCVInserted,
      },
    });
    dialogRef.afterClosed().subscribe(_result => {
      //console.log('The recipîent dialog was closed');
    });
  }

  onMouseOver(index: number) {
    //console.log("Over row: " + index);
    this.mouseoutSubject.next(true);
    this.mouseoverSubject.next(index);
  }

  onMouseOut(index: number) {
    //console.log("Out row:" +row.id);
    this.mouseoutSubject.next(false);
  }

  ngOnDestroy() {
    if (this.mouseoversubscription) this.mouseoversubscription.unsubscribe();
    if (this.mouseoutsubscription) this.mouseoutsubscription.unsubscribe();
    this.stopCountdown();
    this.subscriptions.unsubscribe();
  }
}

function getAsciiCodes(inputString: string) {
  let asciiCodes = [];
  for (let i = 0; i < inputString.length; i++) {
    asciiCodes.push(inputString.charCodeAt(i));
  }
  return asciiCodes;
}

function replaceAsciiCode(inputString: string): string {
  const replacementChar = '�';
  const targetChar = 'é';
  return inputString.split(replacementChar).join(targetChar);
}
